import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';

/**
 * Sesiune 31 iter 20 - PhoneInput cu prefix tara.
 * Reutilizabil pentru Register, SuggestionForm, FirmaDataPage.
 *
 * Validare logica per tara (lungime nationala). Format E.164 simplu (+CC + cifre).
 *
 * Props:
 *  - value: string (E.164 format ex "+40754243893" sau cifre raw)
 *  - onChange: (e164: string) => void
 *  - countryCode: optional initial country code (ex "RO")
 *  - onCountryChange: optional callback (code) => void
 *  - testid: data-testid prefix
 */
const COUNTRIES = [
  { code: 'RO', dial: '40', flag: '🇷🇴', len: 9, name: 'Romania' },
  { code: 'MD', dial: '373', flag: '🇲🇩', len: 8, name: 'Moldova' },
  { code: 'IT', dial: '39', flag: '🇮🇹', len: 10, name: 'Italia' },
  { code: 'DE', dial: '49', flag: '🇩🇪', len: [10, 11], name: 'Germania' },
  { code: 'FR', dial: '33', flag: '🇫🇷', len: 9, name: 'Franta' },
  { code: 'ES', dial: '34', flag: '🇪🇸', len: 9, name: 'Spania' },
  { code: 'GB', dial: '44', flag: '🇬🇧', len: 10, name: 'Marea Britanie' },
  { code: 'AT', dial: '43', flag: '🇦🇹', len: [10, 11], name: 'Austria' },
  { code: 'HU', dial: '36', flag: '🇭🇺', len: 9, name: 'Ungaria' },
  { code: 'BG', dial: '359', flag: '🇧🇬', len: 9, name: 'Bulgaria' },
  { code: 'GR', dial: '30', flag: '🇬🇷', len: 10, name: 'Grecia' },
  { code: 'PL', dial: '48', flag: '🇵🇱', len: 9, name: 'Polonia' },
  { code: 'NL', dial: '31', flag: '🇳🇱', len: 9, name: 'Olanda' },
  { code: 'BE', dial: '32', flag: '🇧🇪', len: 9, name: 'Belgia' },
  { code: 'CH', dial: '41', flag: '🇨🇭', len: 9, name: 'Elvetia' },
  { code: 'PT', dial: '351', flag: '🇵🇹', len: 9, name: 'Portugalia' },
  { code: 'IL', dial: '972', flag: '🇮🇱', len: 9, name: 'Israel' },
  { code: 'US', dial: '1', flag: '🇺🇸', len: 10, name: 'SUA / Canada' },
];

export const parseE164 = (val) => {
  if (!val) return { country: COUNTRIES[0], national: '' };
  const digits = String(val).replace(/[^0-9]/g, '');
  // Try to match longest dial prefix first
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (digits.startsWith(c.dial)) return { country: c, national: digits.slice(c.dial.length) };
  }
  return { country: COUNTRIES[0], national: digits };
};

const isValidLen = (national, len) => {
  if (!national) return false;
  if (Array.isArray(len)) return len.includes(national.length);
  return national.length === len;
};

const PhoneInput = ({ value = '', onChange, testid = 'phone' }) => {
  const { t } = useTranslation('common');
  const initial = useMemo(() => parseE164(value), [value]);
  const [country, setCountry] = React.useState(initial.country);
  const [national, setNational] = React.useState(initial.national);

  React.useEffect(() => {
    const p = parseE164(value);
    setCountry(p.country);
    setNational(p.national);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (c, n) => {
    const cleaned = (n || '').replace(/[^0-9]/g, '');
    const e164 = cleaned ? `+${c.dial}${cleaned}` : '';
    onChange && onChange(e164);
  };

  const onCountry = (e) => {
    const next = COUNTRIES.find((c) => c.code === e.target.value) || COUNTRIES[0];
    setCountry(next);
    emit(next, national);
  };

  const onNational = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, '');
    // Sesiune 31 iter 22: strip "0" leading (european trunk prefix) cand depaseste lungimea asteptata.
    const expected = Array.isArray(country.len) ? Math.max(...country.len) : country.len;
    if (v.startsWith('0') && expected && v.length > expected) {
      v = v.replace(/^0+/, '');
    }
    setNational(v);
    emit(country, v);
  };

  const valid = isValidLen(national, country.len);
  const filled = national.length > 0;
  const expectedLen = Array.isArray(country.len) ? country.len.join('/') : country.len;

  return (
    <div>
      <div className="flex items-stretch gap-2">
        <div className="relative">
          <select
            value={country.code}
            onChange={onCountry}
            className="appearance-none pr-7 pl-2 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid={`${testid}-country`}
            aria-label="Prefix tara"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} +{c.dial} {c.code}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">▼</span>
        </div>
        <div className="relative flex-1">
          <Phone className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="tel"
            inputMode="numeric"
            value={national}
            onChange={onNational}
            placeholder={`${country.len ? (Array.isArray(country.len) ? country.len[0] : country.len) : ''} cifre`}
            className={`w-full pl-7 pr-3 py-2 border rounded-md text-sm ${filled && !valid ? 'border-red-400 bg-red-50' : valid ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300'}`}
            data-testid={`${testid}-input`}
          />
        </div>
      </div>
      {filled && !valid && (
        <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>
          {country.name}: {expectedLen} cifre necesare, ai introdus {national.length}
        </p>
      )}
      {valid && (
        <p className="text-xs text-emerald-600 mt-1">{t('valid_phone_for', { country: country.name, defaultValue: `Numar valid pentru ${country.name}` })}</p>
      )}
    </div>
  );
};

export default PhoneInput;
